package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LoadErrorState
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.loadGuarded
import br.com.vvrefeicoes.portal.core.util.DateTimeProvider
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.CompanyRef
import br.com.vvrefeicoes.portal.domain.model.MonthPerformance
import br.com.vvrefeicoes.portal.domain.model.OrgChartData
import br.com.vvrefeicoes.portal.domain.model.RatingCounts
import br.com.vvrefeicoes.portal.domain.model.SatisfactionMonth
import br.com.vvrefeicoes.portal.domain.model.SatisfactionSnapshot
import br.com.vvrefeicoes.portal.domain.model.UnitSatisfaction
import br.com.vvrefeicoes.portal.domain.model.unidadeKeyOf
import br.com.vvrefeicoes.portal.domain.repository.OrgChartRepository
import br.com.vvrefeicoes.portal.domain.repository.SatisfactionDashboardRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

class SatisfactionDashboardController(
    private val repository: SatisfactionDashboardRepository,
    private val orgChartRepository: OrgChartRepository,
    private val scope: CoroutineScope,
    private val dateTimeProvider: DateTimeProvider = SystemDateTimeProvider(),
    private val loadError: LoadErrorState = LoadErrorState(),
) {
    var loading by mutableStateOf(true)
        private set

    var selection by mutableStateOf(PeriodSelection.current(dateTimeProvider.currentPeriod()))
        private set

    var snapshot by mutableStateOf(SatisfactionSnapshot.Empty)
        private set

    private var started = false
    private var unitFilter: CompanyRef? = null
    private var managerFilter: String? = null
    private var directorFilter: String? = null
    private var authorizedUnits: List<CompanyRef> = emptyList()
    private var orgChartData: OrgChartData? = null

    val isEmpty: Boolean
        get() = snapshot.units.isEmpty() && snapshot.totalVotes == 0

    val totalVotes: Int
        get() = snapshot.totalVotes

    val goalPercent: Double
        get() = snapshot.goalPercent

    val participationPercent: Double
        get() = snapshot.participationPercent

    val satisfactionPercent: Double
        get() = snapshot.satisfactionPercent

    val counts: RatingCounts
        get() = snapshot.counts

    val achieved: Boolean
        get() = snapshot.achieved

    val evolution: List<SatisfactionMonth>
        get() = snapshot.evolution

    val performance: List<MonthPerformance>
        get() = snapshot.performance

    val units: List<UnitSatisfaction>
        get() = snapshot.units

    fun init() {
        if (started) return
        started = true
        scope.launch {
            loadError.loadGuarded { load() }
            loading = false
        }
    }

    fun applyPeriod(newSelection: PeriodSelection) {
        if (newSelection == selection) return
        selection = newSelection
        scope.launch { loadError.loadGuarded { load() } }
    }

    fun applyFilter(
        unit: CompanyRef?,
        manager: String?,
        director: String?,
        authorizedUnits: List<CompanyRef>,
    ) {
        if (unit == unitFilter && manager == managerFilter && director == directorFilter && authorizedUnits == this.authorizedUnits) return
        unitFilter = unit
        managerFilter = manager
        directorFilter = director
        this.authorizedUnits = authorizedUnits
        scope.launch { loadError.loadGuarded { load() } }
    }

    private suspend fun load() {
        val data =
            orgChartData ?: orgChartRepository
                .loadInitial()
                .currentVersion.data
                .also { orgChartData = it }
        val unitId = unitFilter?.let { data.unidadeKeyOf(it) }
        snapshot = repository.load(selection, unitId, managerFilter, directorFilter)
    }
}

@Composable
fun rememberSatisfactionDashboardController(): SatisfactionDashboardController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember {
        SatisfactionDashboardController(
            repository = appGraph.satisfactionDashboardRepository,
            orgChartRepository = appGraph.orgChartRepository,
            scope = scope,
            dateTimeProvider = appGraph.dateTimeProvider,
            loadError = appGraph.loadError,
        )
    }
}
